import { UntrustedApiMethods } from "@cede/types";

export type StoreMethodParams = Record<string, string>;

export type StoreMethodMetaData = {
  methodName: UntrustedApiMethods;
  actionName: string;
  params?: StoreMethodParams;
  apiParamsMapping?: Record<string, string>; // To map the form values to the params expected by the api
};

/**
 * @description Special type markers
 */
export const OPTIONAL_MARKER = "?";
export const ARRAY_MARKER = "[]";
export const SELECT_MARKER = "|";

/**
 * @description Define the separator used to join the array values when the array param is converted to a string
 */
export const ARRAY_SEPARATOR = " ";

/**
 * @description This constant contains the definition of all the forms in the ballpit app. A form corresponds to a method
 * in the Cede.store untrusted API. It is used to generate the forms.
 *
 * When you add a new method you need to define:
 *
 * - The method name (you need to use the same name as the method in the Cede.store untrusted API)
 * - The action name (the name that will be displayed in the UI)
 * - The params (optional)
 * Each param has a name and a type. The type is used to cast the value to the correct type.
 * The type can be: `string`, `number`, `boolean`.
 * You can add an optional marker `?` to make the param optional.
 * You can add an optional marker `[]` to make the param an array.
 * - The apiParamsMapping (optional): This is used to map the form values to the params expected by the api. Sometimes
 * the api expects an object with nested objects, so we need to map the form (which is a flat object) to the correct nested object(s).
 */
export const STORE_API_METHODS: Record<string, StoreMethodMetaData[]> = {
  CONNECTION_SUITE: [
    {
      methodName: "connect",
      actionName: "Connect",
      params: {
        onboard: "boolean",
        callbackUrl: "string?",
      },
    },
    {
      methodName: "disconnect",
      actionName: "Disconnect",
    },
  ],
  PUBLIC_READ_SUITE: [
    {
      methodName: "getMarketPairs",
      actionName: "Market Pairs",
      params: {
        accountId: "string",
      },
    },
    {
      methodName: "getMarketRate",
      actionName: "Market Rate",
      params: {
        accountId: "string",
        pairSymbol: "string",
      },
    },
    {
      methodName: "getNetworks",
      actionName: "Get Networks",
      params: {
        accountId: "string",
        tokenSymbol: "string",
        toDeposit: "boolean",
        toWithdraw: "boolean",
      },
      apiParamsMapping: {
        accountId: "accountId",
        tokenSymbol: "tokenSymbol",
        toDeposit: "opts.toDeposit",
        toWithdraw: "opts.toWithdraw",
      },
    },
    {
      methodName: "depositableTokens",
      actionName: "Depositable Tokens",
      params: {
        accountId: "string",
      },
    },
    {
      methodName: "deposit",
      actionName: "Deposit",
      params: {
        accountId: "string",
        tokenSymbol: "string",
        network: "string?",
      },
    },
    {
      methodName: "retrieveOrder",
      actionName: "Retrieve Order",
      params: {
        accountId: "string",
        orderId: "string",
        pairSymbol: "string",
      },
    },
    {
      methodName: "openOrders",
      actionName: "Open Orders",
      params: {
        accountId: "string",
        pairSymbol: "string",
        since: "number?",
        limit: "number?",
      },
    },
    {
      methodName: "supportedExchanges",
      actionName: "Supported Exchanges",
    },
    {
      methodName: "getPrices",
      actionName: "Get Prices",
    },
    {
      methodName: "getWithdrawalById",
      actionName: "Get Withdrawal By Id",
      params: {
        accountId: "string",
        withdrawalId: "string",
        tokenSymbol: "string",
        timestamp: "number?",
      },
    },
    {
      methodName: "getTradePath",
      actionName: "Get trade path",
      params: {
        accountId: "string",
        from: "string",
        to: "string",
      },
    },
    {
      methodName: "getSupportedTokens",
      actionName: "Get supported tokens",
      params: {
        accountId: "string",
        network: "string",
      },
    },
  ],
  PRIVATE_READ_SUITE: [
    {
      methodName: "vaults",
      actionName: "Vaults",
    },
    {
      methodName: "activeVault",
      actionName: "Active Vault",
    },
    {
      methodName: "supportedExchanges",
      actionName: "Supported Exchanges",
    },
    {
      methodName: "transactions",
      actionName: "Transactions",
      params: {
        vaultId: "string",
        accountIds: "string[]?",
        tokenSymbol: "string?",
        txType: "string?",
        startTime: "number?",
        pageCount: "number?",
        pageIndex: "number?",
      },
    },
    {
      methodName: "balances",
      actionName: "Balances",
      params: {
        vaultId: "string",
        accountIds: "string[]?",
      },
    },
    {
      methodName: "nfts",
      actionName: "NFTs",
      params: {
        accountId: "string",
      },
    },
    {
      methodName: "getMinAmounts",
      actionName: "Get Min Order Amounts",
      params: {
        accountId: "string",
        pairSymbol: "string",
        orderSide: "string",
        price: "string",
      },
    },
    {
      methodName: "withdrawableTokens",
      actionName: "Withdrawable Tokens",
      params: {
        accountId: "string",
        network: "string?",
      },
    },
    {
      methodName: "getAddressBook",
      actionName: "Get Address Book",
    },
    {
      methodName: "getWhitelistedAddresses",
      actionName: "Get Whitelisted Addresses",
      params: {
        accountId: "string",
        tokenSymbol: "string?",
        network: "string?",
      },
    },
    {
      methodName: "verifyKYC",
      actionName: "Verify KYC",
      params: {
        accountId: "string",
        callbackUrl: "string",
        tokenSymbol: "string?",
        network: "string?",
      },
    },
  ],
  WRITE_SUITE: [
    {
      methodName: "prepareOrder",
      actionName: "Prepare Order",
      params: {
        accountId: "string",
        pairSymbol: "string",
        orderSide: "string",
        orderType: "string",
        amount: "string",
        price: "string",
      },
    },
    {
      methodName: "createOrder",
      actionName: "Create Order",
      params: {
        accountId: "string",
        pairSymbol: "string",
        orderSide: "string",
        orderType: "string",
        amount: "string",
        price: "string",
      },
    },
    {
      methodName: "addCex",
      actionName: "Add CEX",
      params: {
        exchangeId: "string",
        readOnlyOnboarding: "boolean?",
        callbackUrl: "string?",
      },
    },
    {
      methodName: "editOrder",
      actionName: "Edit Order",
      params: {
        orderId: "string",
        accountId: "string",
        pairSymbol: "string",
        orderSide: "string?",
        orderType: "string?",
        amount: "string?",
        price: "string?",
      },
    },
    {
      methodName: "cancelOrder",
      actionName: "Cancel Order",
      params: {
        orderId: "string",
        accountId: "string",
        pairSymbol: "string",
      },
    },
    {
      methodName: "prepareWithdrawal",
      actionName: "Prepare Withdrawal",
      params: {
        accountId: "string",
        tokenSymbol: "string",
        amount: "string",
        network: "string",
      },
    },
    {
      methodName: "withdrawToDefi",
      actionName: "Withdraw To Defi address",
      params: {
        accountId: "string",
        tokenSymbol: "string",
        amount: "string",
        network: "string",
        address: "string",
      },
    },
    {
      methodName: "getUserId",
      actionName: "Get the user id",
    },
    {
      methodName: "getEnvironment",
      actionName: "Get the environment",
    },
  ],
};
